// @ts-check
/** What a shipment cost, itemised (freight, cash-on-delivery fee, other such as the courier partner's
 * WhatsApp fee) with the total, and a small editor to enter the figures from the carrier's bill. */
import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import adminShippingService from '../services/adminShippingService';

const rupees = (value) => '₹' + Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const PARTS = [
  { key: 'freight_charge', label: 'Freight' },
  { key: 'cod_charge', label: 'COD fee' },
  { key: 'other_charge', label: 'WhatsApp & other' },
];

/**
 * @param {{shipment: any, onSaved: () => void}} props
 */
const ShipmentCharges = ({ shipment, onSaved }) => {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState(/** @type {Record<string, string>} */ ({}));

  const start = () => {
    setForm(Object.fromEntries(PARTS.map(({ key }) => [key, shipment[key] != null ? String(shipment[key]) : ''])));
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(PARTS.map(({ key }) => [key, form[key] === '' ? 0 : Number(form[key])]));
      await adminShippingService.updateShipment(shipment.id, payload);
      toast.success('Shipping charges saved');
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Failed to save the charges');
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await adminShippingService.refreshCharges(shipment.id);
      toast.success('Shipping cost updated from Shiprocket');
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Failed to refresh the cost');
    } finally {
      setRefreshing(false);
    }
  };

  const itemised = PARTS.filter(({ key }) => shipment[key] != null);

  return (
    <div className="mt-2 text-xs text-muted-foreground">
      {!editing && (
        <>
          {shipment.shipping_charge != null && (
            <p className="text-foreground">
              Shipping cost: <span className="font-medium">{rupees(shipment.shipping_charge)}</span>
            </p>
          )}
          {itemised.map(({ key, label }) => (
            <p key={key}>
              {label}: {rupees(shipment[key])}
            </p>
          ))}
          {shipment.status !== 'cancelled' && (
            <div className="mt-1 flex items-center gap-3">
              <button type="button" onClick={start} className="underline hover:text-foreground">
                {shipment.shipping_charge != null ? 'Edit charges' : 'Add charges'}
              </button>
              {shipment.shiprocket_order_id && (
                <button
                  type="button"
                  onClick={refresh}
                  disabled={refreshing}
                  className="inline-flex items-center gap-1 underline hover:text-foreground disabled:opacity-50"
                >
                  <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh cost
                </button>
              )}
            </div>
          )}
        </>
      )}
      {editing && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            {PARTS.map(({ key, label }) => (
              <label key={key} className="block">
                <span className="block mb-1">{label} ({'₹'})</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-input rounded-lg text-foreground"
                />
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm border border-input rounded-lg">
              Cancel
            </button>
          </div>
          <p>The total is the three added together.</p>
        </div>
      )}
    </div>
  );
};

export default ShipmentCharges;
